import RAPIER, { type World } from '@dimforge/rapier3d-compat';
import * as THREE from 'three';
import { Loop } from '../core/Loop';
import { createRenderer } from '../core/Renderer';
import { createArcadeScene, type ArcadeSceneResult } from '../scenes';
import { AudioSystem } from '../systems/AudioSystem';
import { CameraRig } from '../systems/CameraRig';
import { CelebrationVfx } from '../systems/CelebrationVfx';
import {
  Hud,
  type CoinPusherHudSnapshot,
  type HudCameraPreset,
  type HudOverlay,
  type HudStatusTone,
} from '../systems/Hud';
import {
  CoinInsertionSystem,
  type CoinInsertionDecision,
  type CoinInsertionEvent,
  type CoinInsertionIntent,
  type InsertionLane,
} from '../systems/insertion';
import {
  createMedalPhysicsPerformanceSource,
  DESKTOP_QUALITY_PROFILES,
  DesktopPerformanceGovernor,
  installPerformanceDiagnosticsBridge,
} from '../systems/performance';
import { PayoutTraySystem } from '../systems/payout';
import {
  MedalPhysicsSystem,
  createPusherFixedStepHooks,
  initializeMedalPhysics,
  type MedalOutcome,
} from '../systems/physics';
import {
  SessionProgressionSystem,
  type SessionProgressionEvent,
} from '../systems/progression';
import { PusherPlatform } from '../systems/pusher';
import {
  BonusCardRewardSystem,
  type BonusCardRewardEvent,
} from '../systems/rewards/bonus';
import {
  createRenderPipeline,
  createVisualPolish,
  type RenderPipeline,
  type VisualPolish,
} from '../systems/render';

const MACHINE_FLOOR_Y = 0.38;
const DECK_TOP_Y = 2.18 + MACHINE_FLOOR_Y;
const FALL_RESOLUTION_Y = 1.42 + MACHINE_FLOOR_Y;
const JACKPOT_THRESHOLD = 40;
const JACKPOT_AWARD = 100;
const INITIAL_COIN_COUNT = 48;
const INITIAL_PILE_FRONT_MARGIN = 0.32;
const MAX_COIN_COUNT = 240;
const INITIAL_COIN_VARIANTS = [
  'nickel-10', 'nickel-10', 'nickel-10', 'nickel-10',
  'silver-25', 'silver-25', 'silver-25',
  'brass-5',
] as const;
const BONUS_CARD_DENOMINATIONS = [50, 100, 200, 500, 1000] as const;
const TRAY_BURST_POSITION = new THREE.Vector3(0, 1.42, 1.78);
const JACKPOT_BURST_POSITION = new THREE.Vector3(0, 3.15, 0.45);

type StatusMessage = {
  kicker: string;
  message: string;
  tone: HudStatusTone;
  eventId: string;
};

type PendingCardRespawn = {
  atSeconds: number;
  denominationIndex: number;
};

type PendingImpact = {
  atSeconds: number;
  pan: number;
};

export class Game {
  private readonly automatedQa = navigator.webdriver;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(42, 1, 0.08, 90);
  private readonly audio = new AudioSystem({ masterVolume: 0.8, ambienceEnabled: true });
  private readonly hud = new Hud();
  private readonly cameraRig: CameraRig;
  private readonly loop = new Loop(
    (delta, elapsed) => this.update(delta, elapsed),
    () => this.render(),
  );

  private arcade?: ArcadeSceneResult;
  private world?: World;
  private pusher?: PusherPlatform;
  private physics?: MedalPhysicsSystem;
  private payoutTray?: PayoutTraySystem;
  private progression?: SessionProgressionSystem;
  private bonusRewards?: BonusCardRewardSystem;
  private insertion?: CoinInsertionSystem;
  private renderPipeline?: RenderPipeline;
  private visualPolish?: VisualPolish;
  private celebrationVfx?: CelebrationVfx;
  private performanceGovernor?: DesktopPerformanceGovernor;

  private readonly cleanups: Array<() => void> = [];
  private performanceBridgeCleanup?: () => void;
  private startPromise?: Promise<void>;
  private readonly scratchWorldPosition = new THREE.Vector3();
  private readonly trayRaycaster = new THREE.Raycaster();
  private readonly trayPointer = new THREE.Vector2();
  private readonly pendingCardRespawns: PendingCardRespawn[] = [];
  private readonly pendingImpacts: PendingImpact[] = [];
  private frame = 0;
  private gameTimeSeconds = 0;
  private lastFrameDelta = 0;
  private selectedLane: InsertionLane = 'center';
  private cameraPreset: HudCameraPreset = 'front';
  private paused = false;
  private ready = false;
  private loadingFailed = false;
  private disposed = false;
  private overlay: HudOverlay = 'none';
  private jackpotCelebrationValue = JACKPOT_AWARD;
  private eventSequence = 0;
  private cardRespawnSequence = 0;
  private statusExpiresAt = Number.POSITIVE_INFINITY;
  private hudDirty = true;
  private visualRefreshPending = false;
  private lastCoinBatchRebuilds = -1;
  private lastPusherSoundQuarter = -1;
  private lastTrayCollectableItems = -1;
  private lastTrayBalanceGain = 0;
  private lastTrayCollectionSequence = 0;
  private status: StatusMessage = {
    kicker: 'Machine',
    message: 'Preparing the medal bed…',
    tone: 'neutral',
    eventId: 'loading',
  };

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = createRenderer(canvas, {
      antialias: !this.automatedQa,
      exposure: 1.05,
      shadows: this.automatedQa
        ? false
        : { enabled: true, type: THREE.PCFShadowMap, autoUpdate: true },
    });
    this.cameraRig = new CameraRig(this.camera, canvas, {
      homePreset: 'front-machine',
      initialPreset: 'front-machine',
      constraints: {
        minDistance: 3.1,
        maxDistance: 14,
        minAzimuthAngle: THREE.MathUtils.degToRad(-68),
        maxAzimuthAngle: THREE.MathUtils.degToRad(68),
      },
      defaultTransitionDuration: 0.78,
    });
    this.updateHud();
  }

  start(): Promise<void> {
    this.startPromise ??= this.initializeAndStart();
    return this.startPromise;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.loop.stop();
    for (const cleanup of this.cleanups.splice(0).reverse()) cleanup();
    this.performanceBridgeCleanup?.();
    this.performanceBridgeCleanup = undefined;
    this.performanceGovernor?.dispose();
    this.celebrationVfx?.dispose();
    this.visualPolish?.dispose();
    this.renderPipeline?.dispose();
    this.insertion?.dispose();
    this.bonusRewards?.dispose();
    this.payoutTray?.dispose();
    this.physics?.dispose();
    this.pusher?.dispose();
    this.world?.free();
    this.arcade?.dispose();
    this.cameraRig.dispose();
    this.audio.dispose();
    this.hud.dispose();
    this.renderer.dispose();
    window.__THREE_GAME_DIAGNOSTICS__ = undefined;
    window.__COIN_PUSHER_TEST__ = undefined;
    delete document.documentElement.dataset.gameReady;
  }

  private async initializeAndStart(): Promise<void> {
    try {
      await initializeMedalPhysics();
      if (this.disposed) return;

      this.arcade = createArcadeScene(this.scene, {
        renderer: this.automatedQa ? undefined : this.renderer,
        quality: this.automatedQa ? 'performance' : 'full',
        animateMachinePreview: false,
        environmentMap: !this.automatedQa,
        machineOptions: {
          cabinetArtUrl: `${import.meta.env.BASE_URL}assets/textures/flamingo-carnival-cabinet.png`,
          coinCountScale: 0.72,
        },
      });
      this.arcade.setGameplayInteriorVisible(false);

      this.world = new RAPIER.World({ x: 0, y: -9.81, z: 0 });
      this.pusher = new PusherPlatform({
        world: this.world,
        name: 'Main physical pusher shelf',
        rearPosition: [0, DECK_TOP_Y - 0.075, -0.32],
        dimensions: {
          width: 3.64,
          depth: 0.5,
          thickness: 0.15,
          faceHeight: 0.34,
          faceDepth: 0.1,
          toothDepth: 0.08,
        },
        stroke: 0.34,
        speed: 0.24,
        friction: 0.76,
        restitution: 0.025,
        chromeColor: '#dce7ea',
        accentColor: '#ffc344',
      });
      this.arcade.root.add(this.pusher.object);

      this.physics = new MedalPhysicsSystem({
        world: this.world,
        parent: this.arcade.root,
        configureWorld: true,
        maxCoins: MAX_COIN_COUNT,
        maxCards: 8,
        coinScale: 0.36,
        cardScale: 0.3,
        initialPile: {
          count: INITIAL_COIN_COUNT,
          seed: 'la-fiesta-opening-bed',
          // Silver-forward medals reproduce the dense neutral carpet in the
          // interior reference while sparse brass accents remain easy to read.
          variants: INITIAL_COIN_VARIANTS,
          frontMargin: INITIAL_PILE_FRONT_MARGIN,
          sleeping: true,
        },
        bounds: {
          deckTopY: DECK_TOP_Y,
          halfWidth: 1.87,
          playfieldBackZ: -0.08,
          frontEdgeZ: 0.9,
          sideRailFrontZ: 0.68,
          fallY: FALL_RESOLUTION_Y,
          winChuteHalfWidth: 1,
          sideLossX: 1.84,
        },
        castShadows: false,
        receiveShadows: true,
      });
      this.physics.addStepHooks(createPusherFixedStepHooks(this.pusher));
      this.payoutTray = new PayoutTraySystem({
        world: this.world,
        parent: this.arcade.root,
        coinScale: 0.36,
        cardScale: 0.24,
        floorY: 1.21,
      });
      this.cleanups.push(this.physics.addStepHooks(this.payoutTray.stepHooks));

      this.progression = new SessionProgressionSystem({
        initialCoins: 100,
        frontCoinWinAward: 1,
        coinJackpotContribution: 1,
        jackpotThreshold: JACKPOT_THRESHOLD,
        jackpotAward: JACKPOT_AWARD,
        jackpotOverflowPolicy: 'carry',
        runOutGraceSeconds: 6,
        onEvent: (event) => this.handleProgressionEvent(event),
        onStateChange: () => {
          this.hudDirty = true;
          this.insertion?.refresh();
        },
        onError: (error) => this.reportError(error),
      });

      this.bonusRewards = new BonusCardRewardSystem({
        setSize: 5,
        onEvent: (event) => this.handleBonusEvent(event),
        onStateChange: () => {
          this.hudDirty = true;
        },
        onError: (error) => this.reportError(error),
      });

      this.cleanups.push(this.physics.onOutcome((outcome) => this.handleMedalOutcome(outcome)));
      this.spawnInitialBonusCards();

      this.insertion = new CoinInsertionSystem({
        requestCoin: (intent) => this.handleInsertionRequest(intent),
        readBalance: () => this.progression?.snapshot.balance ?? 0,
        initialLane: 'center',
        cooldownMs: 180,
        repeat: { enabled: true, initialDelayMs: 420, intervalMs: 190 },
        keyboard: true,
        clock: () => this.gameTimeSeconds * 1000,
        onEvent: (event) => this.handleInsertionEvent(event),
        onStateChange: (state) => {
          this.selectedLane = state.selectedLane;
          this.hudDirty = true;
        },
        onError: (error) => this.reportError(error),
      });
      this.cleanups.push(this.insertion.bindButtons(
        {
          left: this.hud.buttons.lanes.left,
          center: this.hud.buttons.lanes.center,
          right: this.hud.buttons.lanes.right,
          drop: this.hud.buttons.insertCoin,
        },
        {
          laneButtons: { dropOnPress: false },
          dropButton: { holdToRepeat: true },
        },
      ));
      this.cleanups.push(this.hud.bindActions({
        onCameraChange: (preset) => this.selectCamera(preset),
        onPauseToggle: () => this.setPaused(!this.paused),
        onResume: () => this.setPaused(false),
        onMuteToggle: () => this.toggleMute(),
        onRestart: () => this.restart(),
        onJackpotContinue: () => this.continueAfterJackpot(),
        onCollectTray: () => this.collectTray(),
      }));
      this.installTrayCollectionInput();

      this.renderPipeline = createRenderPipeline(this.renderer, this.scene, this.camera, {
        postProcessing: !this.automatedQa,
        bloom: { enabled: !this.automatedQa, strength: 0.14, radius: 0.18, threshold: 1.12 },
        maxDpr: this.automatedQa ? 1 : DESKTOP_QUALITY_PROFILES.high.maxDevicePixelRatio,
      });
      this.visualPolish = createVisualPolish(this.scene, this.arcade.machineRuntime, {
        quality: this.automatedQa ? 'performance' : 'full',
        glassOpacity: 0.16,
      });
      this.celebrationVfx = new CelebrationVfx(this.arcade.root);
      this.visualPolish.refreshVisibility();

      this.performanceGovernor = new DesktopPerformanceGovernor({
        renderer: this.renderer,
        scene: this.scene,
        physics: createMedalPhysicsPerformanceSource(this.physics),
        initialTier: 'high',
        adaptiveQuality: true,
        applyQuality: ({ profile }) => this.applyPerformanceProfile(profile),
      });
      this.performanceGovernor.applyInitialQuality();
      this.performanceBridgeCleanup = installPerformanceDiagnosticsBridge(this.performanceGovernor);

      this.ready = true;
      this.loadingFailed = false;
      this.setStatus('Machine ready', 'Choose a lane — time the moving shelf.', 'neutral', 6);
      this.renderPipeline.resize();
      this.installTestBridge();
      document.documentElement.dataset.gameReady = 'true';
      this.hudDirty = true;
      this.updateHud();
      this.publishDiagnostics(true);
      this.loop.start();
    } catch (error) {
      this.loadingFailed = true;
      this.ready = false;
      this.overlay = 'game-over';
      this.setStatus('Machine fault', 'The arcade cabinet could not finish loading.', 'warning');
      this.reportError(error);
      this.updateHud();
    }
  }

  private update(delta: number, elapsed: number): void {
    this.frame += 1;
    this.lastFrameDelta = delta;
    this.performanceGovernor?.beginFrame();

    this.arcade?.update(elapsed, delta);
    this.cameraRig.update(delta);
    this.visualPolish?.update(elapsed);

    if (this.ready && !this.paused) {
      this.gameTimeSeconds += delta;
      this.physics?.update(delta);
      this.updateProgressionDeadline();
      this.updatePendingCardRespawns();
      this.updatePendingImpacts();
      this.updateMechanicalAudio();
      this.celebrationVfx?.update(delta);
    }

    const trayCollectableItems = this.payoutTray?.snapshot.collectableItems ?? 0;
    if (trayCollectableItems !== this.lastTrayCollectableItems) {
      this.lastTrayCollectableItems = trayCollectableItems;
      this.hudDirty = true;
    }

    const rebuilds = this.physics?.telemetry.coinBatchRebuilds ?? -1;
    if (this.visualRefreshPending || rebuilds !== this.lastCoinBatchRebuilds) {
      this.lastCoinBatchRebuilds = rebuilds;
      this.visualRefreshPending = false;
      this.visualPolish?.refreshVisibility();
    }

    if (this.gameTimeSeconds >= this.statusExpiresAt) {
      this.statusExpiresAt = Number.POSITIVE_INFINITY;
      this.setStatus('Machine', 'Watch the shelf, build the edge, and push for the jackpot.', 'neutral');
    }

    this.renderPipeline?.resize();
    if (this.hudDirty) this.updateHud();
    this.publishDiagnostics();
  }

  private render(): void {
    if (!this.renderPipeline) return;
    this.renderPipeline.render(this.lastFrameDelta);
    this.performanceGovernor?.endFrame(this.lastFrameDelta, {
      excluded: this.paused || document.hidden,
    });
  }

  private handleInsertionRequest(intent: CoinInsertionIntent): CoinInsertionDecision {
    if (!this.ready || this.paused || this.overlay !== 'none') {
      return { accepted: false, reason: 'paused', balance: this.progression?.snapshot.balance ?? 0 };
    }
    if (!this.physics || !this.progression || !this.arcade) {
      return { accepted: false, reason: 'disabled', balance: 0 };
    }
    if (this.physics.counts.coins >= this.physics.maxCoins) {
      this.setStatus('Machine full', 'Let the moving shelf clear a little space first.', 'warning', 3);
      return { accepted: false, reason: 'machine-full', balance: this.progression.snapshot.balance };
    }

    const result = this.progression.consumeInsertionIntent(intent);
    const spent = result.events.find((event) => event.type === 'insertion-spent');
    if (!spent || spent.type !== 'insertion-spent') {
      return {
        accepted: false,
        reason: result.reason === 'duplicate-input' ? 'duplicate-input' : 'no-coins',
        balance: result.snapshot.balance,
      };
    }

    const socket = this.arcade.sockets[`machine.socket.coinDrop.${intent.lane}`]
      ?? this.arcade.machineRuntime.sockets[`socket.coinDrop.${intent.lane}`];
    socket?.getWorldPosition(this.scratchWorldPosition);
    const position = socket
      ? this.scratchWorldPosition
      : this.scratchWorldPosition.set(intent.lane === 'left' ? -0.92 : intent.lane === 'right' ? 0.92 : 0, 4.1, -0.55);
    const pan = intent.lane === 'left' ? -0.65 : intent.lane === 'right' ? 0.65 : 0;
    const coin = this.physics.spawnCoin({
      id: spent.dropId,
      seed: `${spent.dropId}:${intent.lane}`,
      variant: 'nickel-10',
      position: [position.x, position.y, position.z],
      linearVelocity: [pan * 0.08, -0.28, 2.2],
      angularVelocity: [2.4 + intent.id % 3, pan * 1.4, 1.8 - intent.id % 2],
      ccd: true,
      ccdSteps: 34,
      source: 'drop',
    });
    if (!coin) {
      this.progression.resolvePendingDrop({ dropId: spent.dropId, atSeconds: this.gameTimeSeconds });
      this.reportError(new Error('The physics coin pool was unexpectedly full after preflight.'));
      return { accepted: false, reason: 'machine-full', balance: this.progression.snapshot.balance };
    }
    this.pendingImpacts.push({ atSeconds: this.gameTimeSeconds + 0.56, pan });
    this.visualRefreshPending = true;
    return { accepted: true, balance: this.progression.snapshot.balance };
  }

  private handleInsertionEvent(event: CoinInsertionEvent): void {
    if (event.type === 'lane-selected') {
      this.selectedLane = event.lane;
      if (event.changed) {
        this.audio.laneSelect(event.lane);
        this.setStatus('Drop lane', `${capitalize(event.lane)} lane selected.`, 'neutral', 2.2);
      }
    } else if (event.type === 'drop-accepted') {
      void this.audio.unlock();
      this.audio.coinInsert(event.intent.lane);
      this.setStatus('Coin away', `${capitalize(event.intent.lane)} lane — watch the moving shelf.`, 'neutral', 2.8);
    } else if (event.type === 'drop-rejected') {
      if (event.reason === 'no-coins') {
        this.audio.noCoins();
        this.setStatus('No coins', 'The tray is empty. Restart to load a fresh stack.', 'warning', 4);
      } else if (event.reason !== 'cooldown') {
        this.audio.error();
      }
    }
    this.hudDirty = true;
    this.publishDiagnostics(true);
  }

  private handleMedalOutcome(outcome: MedalOutcome): void {
    if (!this.progression || !this.bonusRewards) return;
    const pan = THREE.MathUtils.clamp(outcome.position.x / 1.9, -1, 1);

    if (outcome.itemKind === 'card') {
      const denominationIndex = Math.max(0, BONUS_CARD_DENOMINATIONS.indexOf(outcome.denomination));
      this.pendingCardRespawns.push({
        atSeconds: this.gameTimeSeconds + (outcome.result === 'front-win' ? 4.8 : 3.5),
        denominationIndex,
      });
    }

    if (outcome.result === 'front-win' && this.payoutTray) {
      const tray = this.payoutTray.enqueue(outcome);
      this.audio.metalImpact(outcome.itemKind === 'coin' ? 0.28 : 0.38, pan);
      this.audio.stackRustle(outcome.itemKind === 'coin' ? 0.16 : 0.24, pan);
      this.cameraRig.addShakeImpulse(outcome.itemKind === 'coin' ? 1.6 : 2.6, 0.12);
      this.setStatus(
        outcome.itemKind === 'coin' ? 'Coin in prize tray' : 'Bonus in prize tray',
        outcome.itemKind === 'coin'
          ? `${tray.coins} coin${tray.coins === 1 ? '' : 's'} waiting — collect the tray to bank them.`
          : `${tray.cards} bonus card${tray.cards === 1 ? '' : 's'} waiting — collect before the reward is credited.`,
        'win',
        4.2,
      );
    } else {
      this.progression.consumeMedalOutcome(outcome, this.gameTimeSeconds);
      if (outcome.itemKind === 'coin') {
        this.audio.metalImpact(0.22, pan);
        this.setStatus('Side gutter', 'That medal missed the center prize chute.', 'loss', 2.6);
      } else {
        this.bonusRewards.consumeOutcome(outcome);
        this.audio.metalImpact(0.34, pan);
        this.setStatus('Bonus missed', 'The card slipped into a loss gutter. Another will enter soon.', 'loss', 3.2);
      }
    }
    this.visualRefreshPending = true;
    this.hudDirty = true;
    this.publishDiagnostics(true);
  }

  private handleBonusEvent(event: BonusCardRewardEvent): void {
    if (event.type === 'card-collected') {
      this.audio.cardCollect(event.denomination);
      this.cameraRig.addShakeImpulse(5.5, 0.28);
      this.celebrationVfx?.triggerCardWin(TRAY_BURST_POSITION);
      this.setStatus(
        'Bonus card',
        `${event.denomination} prize-credit card collected — ${event.setProgressAfter}/${event.setSize} in this set.`,
        'win',
        4,
      );
    } else if (event.type === 'set-complete') {
      this.audio.setBonus(event.setNumber);
      this.setStatus('Set complete', `Five-card set complete: +${event.extraCoins} extra coins.`, 'win', 5);
    } else if (event.type === 'reward') {
      this.progression?.consumeBonusReward(event, this.gameTimeSeconds);
      this.setStatus(
        'Card reward',
        `+${event.extraCoins} coins and +${event.recommendedJackpotContribution} jackpot progress.`,
        'win',
        4.2,
      );
    }
    this.hudDirty = true;
  }

  private handleProgressionEvent(event: SessionProgressionEvent): void {
    if (event.type === 'jackpot-hit') {
      this.jackpotCelebrationValue = event.awardCoins;
      this.overlay = 'jackpot';
      this.insertion?.setEnabled(false);
      this.audio.jackpotFanfare();
      this.cameraRig.addShakeImpulse(10, 0.75);
      this.celebrationVfx?.triggerJackpot(JACKPOT_BURST_POSITION);
      this.setStatus('Jackpot', `Grand payout: +${event.awardCoins} coins!`, 'jackpot');
    } else if (event.type === 'session-run-out') {
      this.overlay = 'game-over';
      this.insertion?.setEnabled(false);
      this.audio.noCoins();
      this.setStatus('Session over', 'No medals remain in the player tray.', 'warning');
    } else if (event.type === 'run-out-cancelled') {
      this.overlay = 'none';
      this.insertion?.setEnabled(true);
    } else if (event.type === 'session-restarted') {
      this.overlay = 'none';
    }
    this.hudDirty = true;
  }

  private spawnInitialBonusCards(): void {
    if (!this.physics || !this.progression) return;
    const positions = [
      [0.72, 0.035, 0.68],
      [-0.65, 0.055, 0.28],
      [0.02, 0.07, 0.48],
      [0.55, 0.05, 0.18],
      [1.05, 0.04, 0.56],
    ] as const;
    BONUS_CARD_DENOMINATIONS.forEach((denomination, index) => {
      const [x, lift, z] = positions[index];
      this.physics?.spawnCard({
        id: `bonus-card-s${this.progression?.snapshot.session ?? 1}-${index}`,
        denomination,
        seed: `opening-card-${index}`,
        position: [x, DECK_TOP_Y + 0.16 + lift, z],
        yaw: (index - 2) * 0.14,
        sleeping: true,
        source: 'seed',
      });
    });
    this.visualRefreshPending = true;
  }

  private updatePendingCardRespawns(): void {
    if (!this.physics || !this.progression) return;
    for (let index = this.pendingCardRespawns.length - 1; index >= 0; index -= 1) {
      const pending = this.pendingCardRespawns[index];
      if (pending.atSeconds > this.gameTimeSeconds) continue;
      this.pendingCardRespawns.splice(index, 1);
      if (this.physics.counts.cards >= this.physics.maxCards) continue;
      const sequence = this.cardRespawnSequence;
      this.cardRespawnSequence += 1;
      const lane = (sequence % 3) - 1;
      const denomination = BONUS_CARD_DENOMINATIONS[
        (pending.denominationIndex + sequence + 1) % BONUS_CARD_DENOMINATIONS.length
      ];
      this.physics.spawnCard({
        id: `bonus-respawn-s${this.progression.snapshot.session}-${sequence}`,
        denomination,
        seed: `bonus-respawn-${sequence}`,
        position: [lane * 0.72, 3.45, -0.5],
        yaw: (sequence % 5 - 2) * 0.12,
        linearVelocity: [lane * 0.08, -0.2, 2.4],
        angularVelocity: [0.2, lane * 0.35, 0.18],
        sleeping: false,
        source: 'drop',
      });
      this.visualRefreshPending = true;
      this.setStatus('Bonus restock', `${denomination} prize-credit card entering the playfield.`, 'neutral', 2.8);
    }
  }

  private updatePendingImpacts(): void {
    for (let index = this.pendingImpacts.length - 1; index >= 0; index -= 1) {
      const impact = this.pendingImpacts[index];
      if (impact.atSeconds > this.gameTimeSeconds) continue;
      this.pendingImpacts.splice(index, 1);
      this.audio.metalImpact(0.34, impact.pan);
      this.audio.stackRustle(0.18, impact.pan);
    }
  }

  private updateProgressionDeadline(): void {
    if (!this.progression) return;
    const progression = this.progression.snapshot;
    const trayItems = this.payoutTray?.snapshot.totalItems ?? 0;
    if (
      progression.balance === 0
      && (progression.pendingDropCount > 0 || trayItems > 0)
    ) return;
    this.progression.update(this.gameTimeSeconds);
  }

  private collectTray(): void {
    if (
      !this.ready
      || this.paused
      || this.overlay !== 'none'
      || !this.payoutTray
      || !this.progression
      || !this.bonusRewards
    ) return;

    void this.audio.unlock();
    this.audio.buttonClick();
    const progressionBefore = this.progression.snapshot;
    const jackpotsBefore = progressionBefore.jackpot.jackpotsHit;
    const collection = this.payoutTray.collectReady();
    if (collection.outcomes.length === 0) {
      const waiting = this.payoutTray.snapshot.totalItems;
      if (waiting > 0) {
        this.setStatus('Prize tray', 'The winnings are still landing — collect them in a moment.', 'neutral', 2.2);
      }
      return;
    }

    for (const outcome of collection.outcomes) {
      this.progression.consumeMedalOutcome(outcome, this.gameTimeSeconds);
      if (outcome.itemKind === 'card') this.bonusRewards.consumeOutcome(outcome);
    }

    if (collection.coins > 0) {
      this.audio.coinWin(collection.coins, 0);
      this.celebrationVfx?.triggerCoinWin(TRAY_BURST_POSITION);
    }
    this.cameraRig.addShakeImpulse(collection.cards > 0 ? 4.4 : 2.8, 0.2);
    const progressionAfter = this.progression.snapshot;
    const recoveredBalanceCoins =
      progressionAfter.frontCoinWinCoins - progressionBefore.frontCoinWinCoins
      + progressionAfter.bonusCoins - progressionBefore.bonusCoins;
    this.lastTrayBalanceGain = recoveredBalanceCoins;
    this.lastTrayCollectionSequence += 1;
    const jackpotTriggered = progressionAfter.jackpot.jackpotsHit > jackpotsBefore;
    if (!jackpotTriggered) {
      const pieces = [
        collection.coins > 0
          ? `${collection.coins} medal${collection.coins === 1 ? '' : 's'}`
          : '',
        collection.cards > 0
          ? `${collection.cards} bonus card${collection.cards === 1 ? '' : 's'}`
          : '',
      ].filter(Boolean).join(' and ');
      this.setStatus(
        'Tray collected',
        `Recovered +${recoveredBalanceCoins} coin${recoveredBalanceCoins === 1 ? '' : 's'} from ${pieces}.`,
        'win',
        5,
      );
    }
    this.visualRefreshPending = true;
    this.lastTrayCollectableItems = this.payoutTray.snapshot.collectableItems;
    this.hudDirty = true;
    this.publishDiagnostics(true);
  }

  private installTrayCollectionInput(): void {
    const canvas = this.renderer.domElement;
    let pointerDown: Readonly<{ x: number; y: number }> | null = null;
    const onPointerDown = (event: PointerEvent): void => {
      if (event.button === 0) pointerDown = { x: event.clientX, y: event.clientY };
    };
    const onPointerUp = (event: PointerEvent): void => {
      if (!pointerDown || event.button !== 0 || !this.payoutTray) {
        pointerDown = null;
        return;
      }
      const distance = Math.hypot(event.clientX - pointerDown.x, event.clientY - pointerDown.y);
      pointerDown = null;
      if (distance > 7 || this.payoutTray.snapshot.collectableItems <= 0) return;
      const rect = canvas.getBoundingClientRect();
      this.trayPointer.set(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1,
      );
      this.payoutTray.hitTarget.updateWorldMatrix(true, false);
      this.trayRaycaster.setFromCamera(this.trayPointer, this.camera);
      if (this.trayRaycaster.intersectObject(this.payoutTray.hitTarget, false).length > 0) {
        this.collectTray();
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.code !== 'KeyC' || event.repeat) return;
      this.collectTray();
    };
    const clearPointer = (): void => {
      pointerDown = null;
    };
    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('pointercancel', clearPointer);
    window.addEventListener('blur', clearPointer);
    window.addEventListener('keydown', onKeyDown);
    this.cleanups.push(() => {
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointerup', onPointerUp);
      canvas.removeEventListener('pointercancel', clearPointer);
      window.removeEventListener('blur', clearPointer);
      window.removeEventListener('keydown', onKeyDown);
    });
  }

  private updateMechanicalAudio(): void {
    if (!this.pusher) return;
    const quarter = Math.floor(this.pusher.motion.phase * 2) % 4;
    if (quarter === this.lastPusherSoundQuarter) return;
    this.lastPusherSoundQuarter = quarter;
    this.audio.stackRustle(quarter % 2 === 0 ? 0.14 : 0.2, 0);
  }

  private selectCamera(preset: HudCameraPreset, instant = false): void {
    this.audio.buttonClick();
    this.cameraPreset = preset;
    document.documentElement.dataset.cameraPreset = preset;
    const target = preset === 'front'
      ? 'front-machine'
      : preset === 'playfield'
        ? 'playfield-close-up'
        : preset === 'tray'
          ? 'prize-tray'
          : null;
    if (target) {
      if (instant) this.cameraRig.snapToPreset(target);
      else this.cameraRig.focusPreset(target, { duration: 0.72 });
    } else if (instant) {
      this.cameraRig.snapToPreset('front-machine');
    } else {
      this.cameraRig.home({ duration: 0.82 });
    }
    this.setStatus('Camera', preset === 'home' ? 'Returned to the hero view.' : `${capitalize(preset)} view selected.`, 'neutral', 2);
    this.hudDirty = true;
  }

  /** Runs the same fixed Rapier pipeline without waiting on a software-rendered frame. */
  private advancePhysicsForQa(seconds: number): ThreeGameDiagnostics | undefined {
    if (!this.ready || !this.physics || !this.progression || !Number.isFinite(seconds)) {
      return window.__THREE_GAME_DIAGNOSTICS__;
    }
    const fixedStep = this.physics.telemetry.fixedTimeStep;
    const steps = Math.ceil(THREE.MathUtils.clamp(seconds, 0, 120) / fixedStep);
    for (let index = 0; index < steps; index += 1) {
      this.gameTimeSeconds += fixedStep;
      this.physics.stepOnce();
      this.updateProgressionDeadline();
      this.updatePendingCardRespawns();
      this.updatePendingImpacts();
    }
    this.visualRefreshPending = true;
    this.hudDirty = true;
    this.publishDiagnostics(true);
    return window.__THREE_GAME_DIAGNOSTICS__;
  }

  private setPaused(paused: boolean): void {
    if (!this.ready || this.overlay !== 'none' || paused === this.paused) return;
    this.audio.buttonClick();
    this.paused = paused;
    this.insertion?.setPaused(paused);
    if (paused) {
      this.audio.pause();
      this.setStatus('Paused', 'The physical simulation is safely frozen.', 'neutral');
    } else {
      this.audio.resume();
      this.setStatus('Machine', 'The shelf is moving again.', 'neutral', 2.5);
    }
    this.hudDirty = true;
  }

  private toggleMute(): void {
    this.audio.buttonClick();
    this.audio.toggleMute();
    this.hudDirty = true;
  }

  private restart(): void {
    if (!this.ready || !this.physics || !this.progression || !this.bonusRewards || !this.insertion) return;
    this.audio.buttonClick();
    this.overlay = 'none';
    this.paused = false;
    this.pendingCardRespawns.length = 0;
    this.pendingImpacts.length = 0;
    this.cardRespawnSequence = 0;
    this.payoutTray?.clear();
    this.lastTrayCollectableItems = 0;
    this.lastTrayBalanceGain = 0;
    this.lastTrayCollectionSequence = 0;
    this.progression.restart({ atSeconds: this.gameTimeSeconds, coins: 100, jackpotProgress: 'reset' });
    this.bonusRewards.reset({ clearProcessedOutcomeIds: true, clearTelemetry: true });
    this.physics.reset({
      pile: {
        count: INITIAL_COIN_COUNT,
        seed: `restart-bed-${this.progression.snapshot.session}`,
        variants: INITIAL_COIN_VARIANTS,
        frontMargin: INITIAL_PILE_FRONT_MARGIN,
        sleeping: true,
      },
      clearOutcomeTotals: true,
    });
    this.pusher?.reset({ progress: 0, direction: 'forward' });
    this.insertion.reset({ lane: 'center', enabled: true, paused: false, clearTelemetry: true });
    this.selectedLane = 'center';
    this.spawnInitialBonusCards();
    this.visualRefreshPending = true;
    this.audio.resume();
    this.audio.confirm();
    this.setStatus('Fresh tray', '100 coins loaded. Choose a lane and push your luck.', 'neutral', 4);
    this.hudDirty = true;
    this.updateHud();
    this.publishDiagnostics(true);
  }

  private continueAfterJackpot(): void {
    if (this.overlay !== 'jackpot') return;
    this.audio.confirm();
    this.overlay = 'none';
    this.insertion?.setEnabled(true);
    this.setStatus('Jackpot banked', 'The grand payout is in your balance. Keep pushing!', 'jackpot', 4.5);
    this.hudDirty = true;
  }

  private applyPerformanceProfile(profile: (typeof DESKTOP_QUALITY_PROFILES)[keyof typeof DESKTOP_QUALITY_PROFILES]): void {
    this.renderPipeline?.setBloom({ strength: 0.18 * profile.bloomStrengthScale });
    this.renderPipeline?.setPostProcessingEnabled(!this.automatedQa && profile.tier !== 'safe');
    this.scene.traverse((object) => {
      if (!(object instanceof THREE.DirectionalLight) || !object.castShadow) return;
      object.shadow.mapSize.set(profile.primaryShadowMapSize, profile.primaryShadowMapSize);
      object.shadow.needsUpdate = true;
    });
  }

  private setStatus(
    kicker: string,
    message: string,
    tone: HudStatusTone,
    durationSeconds?: number,
  ): void {
    this.eventSequence += 1;
    this.status = {
      kicker,
      message,
      tone,
      eventId: `event-${this.eventSequence}`,
    };
    this.statusExpiresAt = durationSeconds === undefined
      ? Number.POSITIVE_INFINITY
      : this.gameTimeSeconds + durationSeconds;
    this.hudDirty = true;
  }

  private updateHud(): void {
    const progression = this.progression?.snapshot;
    const bonus = this.bonusRewards?.snapshot;
    const insertion = this.insertion?.state;
    const loading = !this.ready && !this.loadingFailed;
    const snapshot: CoinPusherHudSnapshot = {
      balance: progression?.balance ?? 100,
      cards: {
        collected: bonus?.totalCards ?? 0,
        setSize: bonus?.setSize ?? 5,
        completedSets: bonus?.completedSets ?? 0,
      },
      jackpot: {
        value: progression?.jackpot.awardCoins ?? JACKPOT_AWARD,
        progress: progression
          ? progression.jackpot.progress / progression.jackpot.threshold
          : 0,
      },
      sessionWins: progression?.coinsWon ?? 0,
      tray: {
        totalItems: this.payoutTray?.snapshot.totalItems ?? 0,
        coins: this.payoutTray?.snapshot.coins ?? 0,
        cards: this.payoutTray?.snapshot.cards ?? 0,
        collectableItems: this.payoutTray?.snapshot.collectableItems ?? 0,
        lastCollectedCoins: this.payoutTray?.snapshot.lastCollectedCoins ?? 0,
        lastCollectedCards: this.payoutTray?.snapshot.lastCollectedCards ?? 0,
        lastBalanceGain: this.lastTrayBalanceGain,
        collectionSequence: this.lastTrayCollectionSequence,
      },
      status: this.status,
      selectedLane: this.selectedLane,
      cameraPreset: this.cameraPreset,
      paused: this.paused,
      muted: this.audio.muted,
      canInsertCoin: Boolean(
        this.ready
        && !this.paused
        && this.overlay === 'none'
        && progression?.status !== 'run-out'
        && insertion?.ready,
      ),
      canRestart: this.ready,
      loading,
      loadingMessage: 'Polishing chrome, warming the lights, and stacking medals…',
      overlay: this.overlay,
      gameOver: {
        title: this.loadingFailed ? 'Machine Unavailable' : 'Out of Coins',
        summary: this.loadingFailed
          ? 'The cabinet could not initialize. Reload the page to try again.'
          : `You won ${progression?.coinsWon ?? 0} coins and collected ${bonus?.totalCards ?? 0} bonus cards.`,
      },
      jackpotCelebration: {
        value: this.jackpotCelebrationValue,
        message: 'The full cabinet lights up as 100 coins pour into your balance.',
      },
    };
    this.hud.update(snapshot);
    this.hudDirty = false;
  }

  private publishDiagnostics(force = false): void {
    if (!force && this.frame % 15 !== 0) return;
    const pusher = this.pusher?.motion;
    window.__THREE_GAME_DIAGNOSTICS__ = {
      version: 1,
      ready: this.ready,
      frame: this.frame,
      gameTimeSeconds: round(this.gameTimeSeconds),
      paused: this.paused,
      overlay: this.overlay,
      selectedLane: this.selectedLane,
      cameraPreset: this.cameraPreset,
      progression: this.progression?.snapshot ?? null,
      bonus: this.bonusRewards?.snapshot ?? null,
      insertion: this.insertion
        ? { state: this.insertion.state, telemetry: this.insertion.telemetry }
        : null,
      physics: this.physics
        ? { ...this.physics.telemetry, bounds: { ...this.physics.bounds } }
        : null,
      payoutTray: this.payoutTray
        ? {
            ...this.payoutTray.snapshot,
            lastBalanceGain: this.lastTrayBalanceGain,
            collectionSequence: this.lastTrayCollectionSequence,
          }
        : null,
      pusher: pusher && this.pusher
        ? { ...pusher, dimensions: { ...this.pusher.dimensions } }
        : null,
      camera: this.cameraRig.getState(),
      scene: this.arcade?.metadata.initialDiagnostics ?? null,
      renderer: this.renderPipeline?.getDiagnostics() ?? null,
      visualPolish: this.visualPolish?.getDiagnostics() ?? null,
      performance: this.performanceGovernor?.getDiagnostics() ?? null,
      vfx: this.celebrationVfx?.getDiagnostics() ?? null,
    };
  }

  private installTestBridge(): void {
    if (!import.meta.env.DEV) return;
    window.__COIN_PUSHER_TEST__ = Object.freeze({
      snapshot: () => {
        this.publishDiagnostics(true);
        return window.__THREE_GAME_DIAGNOSTICS__;
      },
      drop: (lane: InsertionLane = this.selectedLane) => this.insertion?.requestDrop({ lane, source: 'api' }) ?? null,
      forceCoinWin: () => this.physics?.debugForceOutcome({ itemKind: 'coin', result: 'front-win' }) ?? null,
      forceCoinLoss: () => this.physics?.debugForceOutcome({ itemKind: 'coin', result: 'side-loss', side: 'left' }) ?? null,
      forceCardWin: () => this.physics?.debugForceOutcome({ itemKind: 'card', result: 'front-win' }) ?? null,
      forceCardLoss: () => this.physics?.debugForceOutcome({ itemKind: 'card', result: 'side-loss', side: 'right' }) ?? null,
      forceJackpot: () => this.progression?.contributeToJackpot({
        id: `qa-jackpot-${this.frame}-${this.eventSequence}`,
        source: 'manual',
        amount: this.progression.configuration.jackpotThreshold - this.progression.snapshot.jackpot.progress,
        atSeconds: this.gameTimeSeconds,
      }) ?? null,
      forceGameOver: () => this.forceGameOverForQa(),
      restart: () => this.restart(),
      setCamera: (preset: HudCameraPreset) => this.selectCamera(preset, true),
      advancePhysics: (seconds: number) => this.advancePhysicsForQa(seconds),
      collectTray: () => this.collectTray(),
      prepareReferenceReview: () => this.prepareReferenceReview(),
      prepareBedSideReview: () => this.prepareBedSideReview(),
      setPaused: (paused: boolean) => this.setPaused(paused),
      refreshVisualDiagnostics: () => ({
        scene: this.arcade?.getDiagnostics() ?? null,
        polish: this.visualPolish?.refreshVisibility() ?? null,
      }),
    });
  }

  /** Isolates the authored cabinet for the img2threejs reference contact sheet. */
  private prepareReferenceReview(): void {
    if (!this.arcade) return;
    this.arcade.layers.ground.visible = false;
    this.arcade.layers.foreground.visible = false;
    this.arcade.layers.midground.visible = false;
    this.arcade.layers.far.visible = false;
    this.arcade.layers.sockets.visible = false;
    this.scene.background = new THREE.Color('#e7eaed');
    this.scene.fog = null;
    this.cameraRig.snapToPose({
      position: [0.35, 4.7, 10.6],
      target: [0, 2.78, 0.2],
      fov: 36,
    });
    this.hudDirty = true;
  }

  /** Gives visual QA the same oblique angle the player used to flag the oversized fixed bed. */
  private prepareBedSideReview(): void {
    if (this.arcade) {
      this.arcade.layers.foreground.visible = false;
      this.arcade.layers.midground.visible = false;
      this.arcade.layers.far.visible = false;
      this.arcade.layers.sockets.visible = false;
    }
    this.cameraRig.snapToPose({
      position: [-4.35, 4.35, 4.55],
      target: [0, 2.5, 0.12],
      fov: 32,
    });
    this.hudDirty = true;
  }

  private forceGameOverForQa(): void {
    if (!this.progression || !this.insertion || !this.physics) return;
    this.payoutTray?.clear();
    this.progression.restart({ atSeconds: this.gameTimeSeconds, coins: 1, jackpotProgress: 'reset' });
    this.insertion.reset({ lane: 'center', enabled: true, paused: false, clearTelemetry: true });
    this.insertion.requestDrop({ lane: 'center', source: 'api' });
    const dropId = this.progression.snapshot.pendingDropIds[0];
    if (dropId) this.physics.debugForceOutcome({ id: dropId, itemKind: 'coin', result: 'side-loss' });
    this.progression.update(this.gameTimeSeconds + this.progression.configuration.runOutGraceSeconds + 0.1);
    this.publishDiagnostics(true);
  }

  private reportError(error: unknown): void {
    console.error('[Coin Pusher]', error);
  }
}

function capitalize(value: string): string {
  return value.length === 0 ? value : `${value[0].toUpperCase()}${value.slice(1)}`;
}

function round(value: number): number {
  return Math.round(value * 1000) / 1000;
}
